import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AdminLayout, PublicLayout } from '@/components/layout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { RequireReplayAuth } from '@/components/auth/RequireReplayAuth';
import { RouteErrorBoundary } from '@/components/ErrorBoundary';
import { PageBuildBadge } from '@/components/dev/PageBuildBadge';
import { RouteAnalytics } from '@/components/RouteAnalytics';
import { LoginPage } from '@/pages/auth/LoginPage';
import { SignupPage } from '@/pages/auth/SignupPage';
import { OnboardingPage } from '@/pages/onboarding/OnboardingPage';
import { HomePage } from '@/pages/home/HomePage';
import { MyRacesPage } from '@/pages/home/MyRacesPage';
import { CreateRacePage } from '@/pages/home/CreateRacePage';
import { useAuthStore } from '@/stores/auth';
import { AuthVerify } from '@/pages/auth/AuthVerify';
import { InviteLanding } from '@/pages/invite/InviteLanding';
import { ConfirmLanding } from '@/pages/invite/ConfirmLanding';
import { UnsubscribePage } from '@/pages/invite/UnsubscribePage';
import { TrackClaimPage } from '@/pages/claim/TrackClaimPage';
import { SkipperUploadPage } from '@/pages/regatta/SkipperUploadPage';
import { ProfilePage } from '@/pages/profile/ProfilePage';
import { TermsPage } from '@/pages/legal/TermsPage';
import { PrivacyPage } from '@/pages/legal/PrivacyPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { Toaster } from '@/components/ui/toaster';

// Public pages are code-split too — ReplayViewer statically imports mapbox-gl,
// so lazy-loading it keeps the ~500 KB-gzip mapbox chunk (and the 4k-line
// viewer) out of the entry bundle for every other route.
const ReplayViewer = lazy(() => import('@/pages/race/ReplayViewer').then((m) => ({ default: m.ReplayViewer })));
const RegattaPage = lazy(() => import('@/pages/regatta/RegattaPage').then((m) => ({ default: m.RegattaPage })));
const BoatProfilePage = lazy(() => import('@/pages/boat/BoatProfilePage').then((m) => ({ default: m.BoatProfilePage })));
const SailorProfilePage = lazy(() => import('@/pages/sailor/SailorProfilePage').then((m) => ({ default: m.SailorProfilePage })));
const LandingPage = lazy(() => import('@/pages/LandingPage').then((m) => ({ default: m.LandingPage })));
const RegattaIndexPage = lazy(() =>
  import('@/pages/regattas/RegattaIndexPage').then((m) => ({ default: m.RegattaIndexPage })),
);

// Admin surfaces (dashboard, inbox, session editor, wizard) and the race
// report are code-split — public visitors never download them (bundle-size
// gate: the eager admin cluster alone was pushing the app chunk past 310 KB).
// Internal eval tooling — login-gated, code-split so ordinary users never pay for it.
const MarkingBoardPage = lazy(() => import('@/pages/eval/MarkingBoardPage').then((m) => ({ default: m.MarkingBoardPage })));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard })));
const TrackInboxPage = lazy(() => import('@/pages/admin/TrackInboxPage').then((m) => ({ default: m.TrackInboxPage })));
const PendingRacesPage = lazy(() => import('@/pages/admin/PendingRacesPage').then((m) => ({ default: m.PendingRacesPage })));
const VideoLockerPage = lazy(() => import('@/pages/admin/VideoLockerPage').then((m) => ({ default: m.VideoLockerPage })));
const UsersPage = lazy(() => import('@/pages/admin/UsersPage').then((m) => ({ default: m.UsersPage })));
const IngestionReviewPage = lazy(() => import('@/pages/admin/IngestionReviewPage').then((m) => ({ default: m.IngestionReviewPage })));
const StandingsImportPage = lazy(() => import('@/pages/admin/StandingsImportPage').then((m) => ({ default: m.StandingsImportPage })));
const EntriesPage = lazy(() => import('@/pages/admin/entries/EntriesPage').then((m) => ({ default: m.EntriesPage })));
const EntryListReviewPage = lazy(() => import('@/pages/admin/EntryListReviewPage').then((m) => ({ default: m.EntryListReviewPage })));
const SessionCreatePage = lazy(() => import('@/pages/admin/sessions/SessionCreatePage').then((m) => ({ default: m.SessionCreatePage })));
const SessionDetailPage = lazy(() => import('@/pages/admin/sessions/SessionDetailPage').then((m) => ({ default: m.SessionDetailPage })));
const SessionRaceView = lazy(() => import('@/pages/admin/sessions/SessionRaceView').then((m) => ({ default: m.SessionRaceView })));
const RaceEditorPage = lazy(() => import('@/pages/admin/sessions/RaceEditorPage').then((m) => ({ default: m.RaceEditorPage })));
const RaceDetailPage = lazy(() => import('@/pages/admin/RaceDetailPage').then((m) => ({ default: m.RaceDetailPage })));
const RaceReportPage = lazy(() => import('@/pages/race/RaceReportPage').then((m) => ({ default: m.RaceReportPage })));

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center p-8 text-sm text-muted-foreground">
      Loading…
    </div>
  );
}

/** Wrap a page element with RouteErrorBoundary */
function withErrorBoundary(element: React.ReactElement) {
  return <RouteErrorBoundary>{element}</RouteErrorBoundary>;
}

/**
 * The root, now that both halves have landed:
 *
 *   signed out          -> LandingPage    (#1507) — a page that explains the
 *                          product, instead of a flat list of race numbers.
 *   signed in, sailor   -> HomePage
 *   signed in, ADMIN    -> HomePage too   (#1509) — admins used to be diverted
 *                          to the regatta index on the theory that /admin is
 *                          their home. But everyone who builds this product is
 *                          an admin, so nobody could see what a sailor sees
 *                          without keeping a second account. The console is one
 *                          nav item away ("Admin", rendered only for admins in
 *                          PublicNav / MobileNav / SiteHeader).
 *
 * An admin with no claimed boat therefore sees the claim panel like any other
 * sailor. That is intended — it is the real empty state, and being able to see
 * it is the point of the change.
 */
function RootPage() {
  const { isAuthenticated } = useAuthStore();
  if (isAuthenticated) return withErrorBoundary(<HomePage />);
  return withErrorBoundary(<LandingPage />);
}


export function App() {
  return (
    <>
      <RouteAnalytics />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public pages — shared PublicNav + PublicFooter */}
          <Route element={<PublicLayout />}>
            {/* Root — landing, dashboard or regatta index depending on who's asking */}
            <Route path="/" element={<RootPage />} />
            {/* Kept so the landing page can be previewed while signed in. */}
            <Route path="/landing" element={withErrorBoundary(<LandingPage />)} />
            <Route path="/regattas" element={withErrorBoundary(<RegattaIndexPage />)} />
            <Route path="/my-races" element={withErrorBoundary(<MyRacesPage />)} />
            <Route path="/create-race" element={withErrorBoundary(<CreateRacePage />)} />
            <Route path="/regatta/:slug" element={withErrorBoundary(<RegattaPage />)} />
            <Route path="/boat/:boatId" element={withErrorBoundary(<BoatProfilePage />)} />
            <Route path="/sailor/:sailorId" element={withErrorBoundary(<SailorProfilePage />)} />
            <Route path="/invite/:token" element={withErrorBoundary(<InviteLanding />)} />
            {/* Pre-regatta confirmation routes (SPEC §F3, F5) */}
            <Route path="/confirm/:token" element={withErrorBoundary(<ConfirmLanding />)} />
            <Route path="/confirm/:token/not-me" element={withErrorBoundary(<UnsubscribePage action="not_me" />)} />
            <Route path="/unsubscribe/:token" element={withErrorBoundary(<UnsubscribePage action="unsubscribe" />)} />
            <Route path="/claim/track/:token" element={withErrorBoundary(<TrackClaimPage />)} />
            <Route path="/replay/races/:raceId/report" element={withErrorBoundary(<RaceReportPage />)} />
            <Route path="/regatta/:slug/upload" element={withErrorBoundary(<SkipperUploadPage />)} />
            {/* Profile page */}
            <Route path="/profile" element={withErrorBoundary(<ProfilePage />)} />
            {/* Legal pages */}
            <Route path="/terms" element={withErrorBoundary(<TermsPage />)} />
            <Route path="/privacy" element={withErrorBoundary(<PrivacyPage />)} />
            {/* 404 catch-all (inside PublicLayout so it gets nav + footer) */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* Viewer — fullscreen, no layout wrapper (Mapbox GL needs special handling).
              Signed-in only, EXCEPT links carrying camera state, which our
              cut-sheet tooling generates — see RequireReplayAuth. */}
          <Route
            path="/regatta/:slug/:div/race/:num"
            element={
              <RequireReplayAuth>
                <ReplayViewer />
              </RequireReplayAuth>
            }
          />
          <Route
            path="/view/:raceId"
            element={withErrorBoundary(
              <RequireReplayAuth>
                <ReplayViewer />
              </RequireReplayAuth>,
            )}
          />

          {/* Internal eval tooling — the marking board (Jason, 2026-08-22).
              Login-gated like the rest of staging; not part of the admin
              area because markers are not necessarily admins. */}
          <Route
            path="/eval/marking"
            element={withErrorBoundary(
              <ProtectedRoute>
                <MarkingBoardPage />
              </ProtectedRoute>,
            )}
          />

          {/* Auth */}
          <Route path="/login" element={withErrorBoundary(<LoginPage />)} />
          <Route path="/signup" element={withErrorBoundary(<SignupPage />)} />
          <Route path="/onboarding" element={withErrorBoundary(<OnboardingPage />)} />
          <Route path="/auth/verify" element={withErrorBoundary(<AuthVerify />)} />

          {/* Admin routes — require authentication */}
          <Route
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/admin" element={withErrorBoundary(<AdminDashboard />)} />
            <Route path="/admin/inbox" element={withErrorBoundary(<TrackInboxPage />)} />
            {/* #1478: the queue of races the public sent us to build. */}
            <Route path="/admin/pending-races" element={withErrorBoundary(<PendingRacesPage />)} />
            <Route path="/admin/video-locker" element={withErrorBoundary(<VideoLockerPage />)} />
            <Route path="/admin/ingestion" element={withErrorBoundary(<IngestionReviewPage />)} />
            <Route path="/admin/standings-import" element={withErrorBoundary(<StandingsImportPage />)} />
            <Route path="/admin/users" element={withErrorBoundary(<UsersPage />)} />
            <Route path="/admin/regattas/:regattaId/entries" element={withErrorBoundary(<EntriesPage />)} />
            <Route path="/admin/regattas/:regattaId/documents/:docId/review-entries" element={withErrorBoundary(<EntryListReviewPage />)} />
            <Route path="/admin/races/:raceId" element={withErrorBoundary(<RaceDetailPage />)} />
            {/* Old wizard removed — Sprint CS2. Race setup now via /admin/sessions/:id/edit */}
            <Route path="/admin/sessions/new" element={withErrorBoundary(<SessionCreatePage />)} />
            <Route path="/admin/sessions/:sessionId" element={withErrorBoundary(<SessionDetailPage />)} />
          </Route>

          {/* Session race viewer — auth required, fullscreen (no AdminLayout) */}
          <Route
            path="/admin/sessions/:sessionId/race/:raceId"
            element={
              <ProtectedRoute>
                {withErrorBoundary(<SessionRaceView />)}
              </ProtectedRoute>
            }
          />

          {/* Session editor — full-day view, race creation, time trimming */}
          <Route
            path="/admin/sessions/:sessionId/edit"
            element={
              <ProtectedRoute>
                {withErrorBoundary(<RaceEditorPage />)}
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
      <PageBuildBadge />
      <Toaster />
    </>
  );
}
